// @flow
import styled from 'styled-components';
import {
  getButtonColorDefaultTheme,
  getButtonColorWithTheme,
  inDarkMode
} from '../../themes/common';
import {lighten} from 'polished';

/**
 * Default Button Style is
 * Large button, Filled, Primary Color, Not Stretched, Not Disabled
 */
const ButtonBaseStyle = `
  display: inline-block;
  border-radius: 1rem;
  cursor: pointer;
  user-select: none;

  &:disabled {
    pointer-events: none;
    opacity: .6;
  }
`;

const LargeButtonStyle = `
  padding: 1.25rem;
  font-size: 1.25rem;
  border: 2px solid transparent;
  
`;

const MediumButtonStyle = `
  padding: 0.75rem 1.25rem;
  font-size: 1.167rem;
  border: 1px solid transparent;
`;

const SmallButtonStyle = `
  padding: 0.5rem 1rem;
  font-size: 1rem;
  border: 1px solid transparent;
`;

const OutlineStyle = ({ buttonType = 'primary', theme }) => {
  const color = getButtonColorWithTheme(buttonType, theme);

  const hoverShade = inDarkMode(theme) ? lighten(0.1, color.hex) : lighten(0.5, color.hex);
  const textColor = inDarkMode(theme) ? theme.ThemeColors.TEXT : color;

  return `
    background: transparent;
    color: ${textColor.hex};
    outline: transparent;
    border-color: ${color.hex};

    &:active {
      background-color: transparent;
    }
    &:hover {
      background-color: ${hoverShade};
    }
  `;
};

const NoFillStyle = ({ buttonType = 'primary', theme, glowHover }) => {
  const textColor = theme.ThemeColors.TEXT;
  const color = inDarkMode(theme)
    ? theme.ThemeColors.DARK
    : theme.ThemeColors.LIGHT;
    // FIXME buttonText is real, buttonColor is not
  const glowHoverSettings = `
      color: ${textColor.hover()};
      text-shadow: 0 0 ${inDarkMode ? '1.5px' : '1px'} ${textColor.hover()};
  `;

  const hoverSettings = `
      border-color: transparent;
      background-color: ${color.hex};
      color: ${color.buttonColor};
  `;

  const activeSettings = `
      border-color: transparent;
      background-color: ${color.X900};
      color: ${color.buttonColor};
  `;

  return `
    border-color: transparent;
    background: transparent;  
    color: ${textColor.hex};

    &:active {
      ${activeSettings}
    }
    &:hover {
      ${glowHover ? glowHoverSettings : hoverSettings}
  `;
};

const ColorStyle = ({ buttonType = 'primary', theme, active }) => {
  const color = getButtonColorWithTheme(buttonType, theme);

  if (!inDarkMode(theme) && buttonType === 'tertiary') {
    return OutlineStyle({ buttonType: 'primary', theme, active });
  }

  const activeSettings = `
      background-color: ${color.X600};
      border-color: ${color.X600};
  `;

  const hoverSettings = `
    &:hover {
      background-color: ${color.X600};
      border-color: ${color.X600};
    }
  `;

  return `
    color: ${color.buttonText};
    background-color: ${color.hex};
    border-color: ${color.hex};
    
    ${active ? activeSettings : ''}
    
    &:active {
      background-color: ${color.X900};
      border-color: ${color.X900};
    }
    ${!active ? hoverSettings : ''}

  `;
};

const DisabledStyle = `
  opacity: .8;
`;

const stretchStyle = `
  width: 100%;
  justify-content: center;
`;

const TransparentStyle = `
  background: transparent;
  border-color: transparent;
  &:hover {
    background: transparent;
    border-color: transparent;
  }
`;

type BaseButtonProps = {
  small?: boolean,
  glowHover?: boolean,
  disabled?: boolean,
  stretched?: boolean,
  fill?: 'outline' | 'none' | '' | 'default',
  active?: boolean,
  children?: any,
  strictStyle?: boolean, // Set this to default true, if we want loose styling we will set it to off
  buttonType?: | 'primary'
    | 'secondary'
    | 'warning'
    | 'danger'
    | 'light'
    | 'dark'
    | 'success'
    | 'info',
  htmlType?: String,
  size?: 'small' | 'medium' | 'large',
};

/**
 * Ok this button has a lot of dynamic style props:
 * - strictStyle: Adds && to the definition to make the styling rules stronger, default: TRUE
 * - ColorStyle: Takes the current theme and looks up the color corresponding with buttonType, default: PRIMARY
 * - size: Self explanatory, applies font-size, padding, dimensions based on param, default: MEDIUM
 * - disabled: Self-explanatory, default: FALSE
 * - stretched: Applies width 100% to the button, default: FALSE
 * - fill: Whether the button should be normal fill, outline only, or no fill, default: undefined aka fill
 * - transparent: Sometimes we need to have a button, to function as a button, but to be transparent. Shruggie. default: FALSE
 */

const Button = styled.button.attrs({
  'aria-label': 'Button',
  'data-cy': (props => props.cyid || 'button'), // cyid is cypressid which translates into a data-cy prop for e2e testing
})`
  ${({ strictStyle }) => (strictStyle === false ? '' : '&& {')}
 
  ${ButtonBaseStyle}
  
  ${({ size }) =>
    size === 'large'
      ? LargeButtonStyle
      : size === 'small'
      ? SmallButtonStyle
      : MediumButtonStyle}
  ${ColorStyle}

  ${({ disabled }) => (disabled ? DisabledStyle : '')}
  ${({ stretched }) => (stretched ? stretchStyle : '')}
  
  ${({ fill }) =>
    fill === 'outline' ? OutlineStyle : fill === 'none' ? NoFillStyle : ''}
  ${({ transparent }) => (transparent ? TransparentStyle : '')}

  ${({ strictStyle }) => (strictStyle === false ? '' : '}')}

`;

export default Button;



// WEBPACK FOOTER //
// ./web_modules/trustar-2.0/components/Button/BaseButton.js